// Camera profiles and blends (decision 0043: "named camera profiles as data with timed blends (the Cinemachine
// model)"). A profile is a flat record of numbers the game writes in its content; the stack picks the active one and
// blends every number from where the camera is now to the new profile's over a set time.
//
// Layers: requests go to named layers in the game's order (default: mode, context, overlay). The highest layer with a
// request wins, so a dialogue on the overlay layer covers whatever the controller's mode asked for, and releasing it
// blends back to that mode's profile without the game tracking what was under it. A controller's control handoff
// (a mount, a climb) requests on the mode layer; the game's own requests (combat, dialogue, a menu) go higher.
//
// Blends run on real time (a frame's real dt, not the scaled one), so a blend into a pause menu finishes while the
// sim is paused, and a blend's length is the same at any frame rate.
import { CAMERA_FAULTS } from "./faults";
import { smoothstep } from "./math";

/** Blend times within this of done are done (s). */
const EPS = 1e-9;

/** Every number a profile sets. All are blended. */
export interface CameraProfile {
  /** The arm's length when nothing is in the way (m). */
  distance: number;
  /** The pitch the profile rests at (rad, > 0 above the pivot); a blend into the profile carries the pitch here. */
  pitch: number;
  /** The player's pitch range (rad). */
  pitchMin: number;
  pitchMax: number;
  /** Vertical field of view (degrees). */
  fov: number;
  /** The pivot's height above the target's position (m). */
  pivotHeight: number;
  /** The pivot's offset to the camera's right (m): an over-the-shoulder framing. */
  shoulder: number;
  /** Follow rates of the pivot (1/s, exponential): horizontal, vertical on the ground, vertical in the air. */
  followH: number;
  followV: number;
  followVAir: number;
  /** Falling faster than this (m/s), the vertical follow is the ground rate again, so a long drop stays in frame. */
  fastFall: number;
  /** Look-ahead: the pivot leads by velocity × leadTime (s), at most leadMax (m), eased at leadRate (1/s). */
  leadTime: number;
  leadMax: number;
  leadRate: number;
  /** Drift back behind the target: starts recentreDelay s after the last look input, at recentreRate (1/s), ramped
   *  in over recentreRamp s after the target stops turning, only above recentreSpeed (m/s). 0 rate turns it off. */
  recentreDelay: number;
  recentreRate: number;
  recentreRamp: number;
  recentreSpeed: number;
  /** The target counts as turning above this facing rate (rad/s). */
  turningRate: number;
  /** With a focus point set (a combat target, a speaker): the share of the way the pivot moves towards it (0..1),
   *  and the rate (1/s) the yaw turns to face it (0: free orbit). */
  focusWeight: number;
  focusYaw: number;
  /** The default blend time into this profile (s). A request may override it. */
  blendIn: number;
}

/** The profile's numbers, in a fixed order (blending and copying walk this list). */
export const PROFILE_KEYS = [
  "distance",
  "pitch",
  "pitchMin",
  "pitchMax",
  "fov",
  "pivotHeight",
  "shoulder",
  "followH",
  "followV",
  "followVAir",
  "fastFall",
  "leadTime",
  "leadMax",
  "leadRate",
  "recentreDelay",
  "recentreRate",
  "recentreRamp",
  "recentreSpeed",
  "turningRate",
  "focusWeight",
  "focusYaw",
  "blendIn",
] as const satisfies readonly (keyof CameraProfile)[];

/** Blend two profiles: a + (b - a) × w for every number. */
export function lerpProfile(a: CameraProfile, b: CameraProfile, w: number, out: CameraProfile = { ...a }): CameraProfile {
  for (const k of PROFILE_KEYS) out[k] = a[k] + (b[k] - a[k]) * w;
  return out;
}

/** Check a profile has every number, finite. Returns the problems (empty when fine). */
export function checkProfile(p: Partial<Record<keyof CameraProfile, unknown>>): string[] {
  const out: string[] = [];
  for (const k of PROFILE_KEYS) if (typeof p[k] !== "number" || !Number.isFinite(p[k])) out.push(`${k} is not a finite number`);
  if (out.length === 0) {
    const q = p as CameraProfile;
    if (q.pitchMin > q.pitchMax) out.push("pitchMin is above pitchMax");
    if (q.distance <= 0) out.push("distance must be > 0");
    if (q.blendIn < 0) out.push("blendIn must be >= 0");
  }
  return out;
}

/** What the stack is doing now. */
export interface BlendState {
  /** The active profile's name. */
  active: string;
  /** The layer it was requested on. */
  layer: string;
  /** The profile being blended from (null when no blend has run). */
  from: string | null;
  /** The blend's progress 0..1 (1 when settled) and its length (s). */
  progress: number;
  duration: number;
  /** Real seconds since the blend started. */
  elapsed: number;
  /** Requests by layer. */
  layers: Record<string, string | null>;
}

/** Options for a request or a release. */
export interface BlendOptions {
  /** The layer (default the lowest, usually "mode"). */
  layer?: string;
  /** The blend time (s); default the incoming profile's `blendIn`. 0 cuts. */
  blend?: number;
}

/**
 * The profile stack: layered requests and timed blends over a table of named profiles. `update(realDt)` once per
 * rendered frame returns the blended numbers.
 */
export class ProfileStack {
  private readonly profiles: Map<string, CameraProfile>;
  private readonly layerOrder: readonly string[];
  private readonly requests = new Map<string, string>();
  private current: CameraProfile;
  private start: CameraProfile;
  private activeName: string;
  private activeLayer: string;
  private fromName: string | null = null;
  private duration = 0;
  private elapsed = 0;

  constructor(profiles: Record<string, CameraProfile>, o: { initial: string; layers?: readonly string[] }) {
    this.profiles = new Map(Object.entries(profiles));
    for (const [name, p] of this.profiles) {
      const bad = checkProfile(p);
      if (bad.length) throw new Error(`camera profile "${name}": ${bad.join("; ")}`);
    }
    this.layerOrder = o.layers ?? ["mode", "context", "overlay"];
    if (this.layerOrder.length === 0) throw new Error("camera profiles: at least one layer");
    const first = this.get(o.initial);
    this.requests.set(this.layerOrder[0]!, o.initial);
    this.current = { ...first };
    this.start = { ...first };
    this.activeName = o.initial;
    this.activeLayer = this.layerOrder[0]!;
  }

  /** The profile names the stack knows. */
  names(): string[] {
    return [...this.profiles.keys()];
  }

  /** A profile's numbers (throws on an unknown name). */
  get(name: string): CameraProfile {
    const p = this.profiles.get(name);
    if (!p) throw new Error(`camera profile "${name}" is not defined (have ${[...this.profiles.keys()].join(", ")})`);
    return p;
  }

  /** Ask for a profile on a layer. If that changes the active profile, a blend starts from the camera's numbers now. */
  request(name: string, o: BlendOptions = {}): void {
    this.get(name);
    const layer = this.layerOf(o.layer);
    this.requests.set(layer, name);
    this.retarget(o.blend);
  }

  /** Clear a layer's request; the next layer down takes over (the lowest layer can't be cleared). */
  release(layer: string, o: Omit<BlendOptions, "layer"> = {}): void {
    const l = this.layerOf(layer);
    if (l === this.layerOrder[0]) return;
    this.requests.delete(l);
    this.retarget(o.blend);
  }

  /** Advance the blend by a frame's real seconds; returns the blended numbers (the same object every call). */
  update(realDt: number): CameraProfile {
    if (this.elapsed < this.duration) {
      this.elapsed += CAMERA_FAULTS.blendCountsFrames ? 1 / 60 : Math.max(0, realDt);
      // (a float sum of frame times lands a hair short of a whole number of frames: count that as done)
      if (this.elapsed >= this.duration - EPS) this.elapsed = this.duration;
      lerpProfile(this.start, this.get(this.activeName), smoothstep(this.progressNow()), this.current);
    }
    return this.current;
  }

  /** The blended numbers as of the last update. */
  get value(): CameraProfile {
    return this.current;
  }

  /** Whether a blend is still running. */
  get blending(): boolean {
    return this.elapsed < this.duration;
  }

  state(): BlendState {
    const layers: Record<string, string | null> = {};
    for (const l of this.layerOrder) layers[l] = this.requests.get(l) ?? null;
    return { active: this.activeName, layer: this.activeLayer, from: this.fromName, progress: this.progressNow(), duration: this.duration, elapsed: this.elapsed, layers };
  }

  /** JSON-safe requests by layer (a save keeps which profiles were asked for, not the blend in flight). */
  snapshot(): Record<string, string> {
    return Object.fromEntries(this.requests);
  }

  /** Put back saved requests and cut to the active profile. */
  restore(s: Record<string, string>): void {
    this.requests.clear();
    for (const l of this.layerOrder) {
      const name = s[l];
      if (name === undefined) continue;
      this.get(name);
      this.requests.set(l, name);
    }
    if (!this.requests.has(this.layerOrder[0]!)) this.requests.set(this.layerOrder[0]!, this.activeName);
    this.retarget(0, true);
  }

  private progressNow(): number {
    return this.duration <= 0 ? 1 : Math.min(1, this.elapsed / this.duration);
  }

  private layerOf(layer: string | undefined): string {
    const l = layer ?? this.layerOrder[0]!;
    if (!this.layerOrder.includes(l)) throw new Error(`camera profiles: unknown layer "${l}" (have ${this.layerOrder.join(", ")})`);
    return l;
  }

  private retarget(blend: number | undefined, cut = false): void {
    let name = this.activeName;
    let layer = this.activeLayer;
    for (let i = this.layerOrder.length - 1; i >= 0; i--) {
      const r = this.requests.get(this.layerOrder[i]!);
      if (r !== undefined) {
        name = r;
        layer = this.layerOrder[i]!;
        break;
      }
    }
    this.activeLayer = layer;
    if (name === this.activeName && !cut) return;
    this.fromName = this.activeName;
    this.activeName = name;
    this.start = { ...this.current };
    this.duration = Math.max(0, blend ?? this.get(name).blendIn);
    this.elapsed = 0;
    if (this.duration === 0) lerpProfile(this.start, this.get(name), 1, this.current);
  }
}
